// ─────────────────────────────────────────────────────────────
//  ProtocoolForm – oeffentliche, passwortgeschuetzte Share-Seite.
//
//  Wird ueber /protocool/:slug/:linkKey geoeffnet. Der Team-Copywriter /
//  Outreach-Manager kommt mit Link + Passwort rein und fuellt das Formular im
//  Call aus. Antworten landen ueber die Cloud Functions am Kundenobjekt
//  (customers/<slug>/protocool/<linkKey>). Liest/schreibt NIE direkt aus der DB.
// ─────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import {
  getProtocoolForm,
  getProtocoolLink,
  formCompletion,
  isFieldFilled,
  isFieldVisible,
  allFormFields,
  ratingVerdict,
  checklistScore,
} from './protocoolConfig';
import './ProtocoolForm.css';

const GET_FN_URL =
  process.env.REACT_APP_PROTOCOOL_GET_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/getProtocoolForm';
const SUBMIT_FN_URL =
  process.env.REACT_APP_PROTOCOOL_SUBMIT_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/submitProtocoolForm';
const UPLOAD_FN_URL =
  process.env.REACT_APP_PROTOCOOL_UPLOAD_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/uploadProtocoolProtocol';

// Cryptische „Entschlüssel"-Animation für die Gate-Überschrift (wie im Research).
const SCRAMBLE_GLYPHS = '!<>-_\\/[]{}=+*^?#·•01ABCDEF';
const ScrambleText = ({ text }) => {
  const [display, setDisplay] = useState(text);
  useEffect(() => {
    let frame = 0;
    const totalFrames = 46;
    let raf;
    const tick = () => {
      frame += 1;
      const revealed = Math.floor((frame / totalFrames) * text.length);
      const next = text
        .split('')
        .map((ch, i) => {
          if (ch === ' ') return ' ';
          if (i < revealed) return ch;
          return SCRAMBLE_GLYPHS[Math.floor(Math.random() * SCRAMBLE_GLYPHS.length)];
        })
        .join('');
      setDisplay(next);
      if (frame < totalFrames) raf = requestAnimationFrame(tick);
      else setDisplay(text);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text]);
  return <span className="pcf-scramble">{display}</span>;
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || '');
      const comma = raw.indexOf(',');
      resolve(comma >= 0 ? raw.slice(comma + 1) : raw);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Holt das Admin-Token (falls eingeloggt) – wartet einmalig auf den Auth-State.
function resolveIdToken() {
  return new Promise((resolve) => {
    const current = auth && auth.currentUser;
    if (current) {
      current.getIdToken().then(resolve).catch(() => resolve(null));
      return;
    }
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      if (user) user.getIdToken().then(resolve).catch(() => resolve(null));
      else resolve(null);
    });
  });
}

const pwStorageKey = (slug, linkKey) => `pc-pw-${slug}-${linkKey}`;

// Stabile ID fuer einen E-Mail-Text (fuer Dedup beim Sync ins Research Center).
const makeTextId = () => `pc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const ProtocoolForm = () => {
  const { slug, linkKey } = useParams();
  const link = getProtocoolLink(linkKey);
  const form = getProtocoolForm(linkKey);

  const [phase, setPhase] = useState('loading'); // loading | gate | form | error
  const [errorMsg, setErrorMsg] = useState('');
  const [gateError, setGateError] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [responses, setResponses] = useState({});
  const [filledBy, setFilledBy] = useState('');
  const [submittedAt, setSubmittedAt] = useState(null);
  const [activePassword, setActivePassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);
  const [protocolBusy, setProtocolBusy] = useState(false);
  const [protocolError, setProtocolError] = useState('');

  const completion = useMemo(() => formCompletion(form, responses), [form, responses]);

  // Sichtbare, aber noch leere Felder – fuer den Hinweis beim Speichern.
  const missingFields = useMemo(
    () =>
      allFormFields(form)
        .filter((f) => isFieldVisible(f, responses))
        .filter((f) => !isFieldFilled(responses[f.id]))
        .map((f) => ({ id: f.id, label: f.label, required: !!f.required })),
    [form, responses]
  );
  const missingRequired = useMemo(
    () => missingFields.filter((f) => f.required),
    [missingFields]
  );

  // noindex: diese Seiten sollen nicht in Suchmaschinen landen.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const fetchForm = useCallback(
    async (password) => {
      const token = await resolveIdToken();
      const resp = await fetch(GET_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ slug, linkKey, password: password || '' }),
      });
      const data = await resp.json().catch(() => ({}));
      return { status: resp.status, data };
    },
    [slug, linkKey]
  );

  // Erstes Laden: mit gespeichertem Passwort (oder Admin-Token) versuchen.
  useEffect(() => {
    if (!form || !link || !link.active) return;
    let cancelled = false;
    (async () => {
      const stored = localStorage.getItem(pwStorageKey(slug, linkKey)) || '';
      const { status, data } = await fetchForm(stored);
      if (cancelled) return;
      if (status === 200 && data.ok) {
        setCompanyName(data.companyName || slug);
        setResponses(data.responses || {});
        setFilledBy(data.filledBy || '');
        setSubmittedAt(data.submittedAt || null);
        setActivePassword(stored);
        setPhase('form');
      } else if (status === 401 || status === 403) {
        if (stored) localStorage.removeItem(pwStorageKey(slug, linkKey));
        setPhase('gate');
      } else if (status === 404) {
        setErrorMsg(data.error || 'Dieses Protokoll ist nicht verfügbar.');
        setPhase('error');
      } else {
        setErrorMsg(data.error || 'Etwas ist schiefgelaufen. Bitte versuch es erneut.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [form, link, slug, linkKey, fetchForm]);

  const submitGate = async (e) => {
    e.preventDefault();
    setGateError('');
    const pw = passwordInput.trim();
    if (!pw) { setGateError('Bitte gib das Passwort ein.'); return; }
    const { status, data } = await fetchForm(pw);
    if (status === 200 && data.ok) {
      localStorage.setItem(pwStorageKey(slug, linkKey), pw);
      setCompanyName(data.companyName || slug);
      setResponses(data.responses || {});
      setFilledBy(data.filledBy || '');
      setSubmittedAt(data.submittedAt || null);
      setActivePassword(pw);
      setPhase('form');
    } else if (status === 403) {
      setGateError('Falsches Passwort. Bitte prüfe es und versuch es erneut.');
    } else if (status === 401) {
      setGateError('Passwort erforderlich.');
    } else {
      setGateError(data.error || 'Etwas ist schiefgelaufen. Bitte versuch es erneut.');
    }
  };

  const setField = (fieldId, value) => {
    setResponses((prev) => ({ ...prev, [fieldId]: value }));
    setSavedTick(false);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setSavedTick(false);
    try {
      const token = await resolveIdToken();
      const resp = await fetch(SUBMIT_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ slug, linkKey, password: activePassword || '', responses, filledBy }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.status === 200 && data.ok) {
        setSubmittedAt((prev) => prev || data.submittedAt || Date.now());
        setSavedTick(true);
        setTimeout(() => setSavedTick(false), 2600);
      } else {
        setErrorMsg(data.error || 'Konnte nicht speichern. Bitte versuch es erneut.');
      }
    } catch (err) {
      setErrorMsg('Netzwerkfehler. Bitte versuch es erneut.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render-Bausteine ──────────────────────────────────────────
  const renderField = (field) => {
    const value = responses[field.id];

    if (field.type === 'rating') {
      const min = field.min || 1;
      const max = field.max || 10;
      const scale = [];
      for (let i = min; i <= max; i++) scale.push(i);
      const current = Number(value);
      const hasVal = isFieldFilled(value);
      const verdict = hasVal ? ratingVerdict(current, max, field.verdictVariant) : null;
      // Tonwert des Meters: niedrig = rot, mittel = amber, hoch = gruen.
      const tone = !hasVal ? 'none' : verdict ? verdict.tone : 'mid';
      return (
        <div className="pcf-rating">
          <div className={`pcf-meter pcf-meter-${tone}`}>
            <div className="pcf-meter-track">
              {scale.map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-label={String(n)}
                  className={`pcf-meter-seg ${hasVal && n <= current ? 'pcf-meter-on' : ''}`}
                  onClick={() => setField(field.id, n)}
                />
              ))}
            </div>
            <div className="pcf-meter-value">
              {hasVal ? <><span className="pcf-meter-num">{current}</span><span className="pcf-meter-max">/{max}</span></> : <span className="pcf-meter-hint">Tippen zum Bewerten</span>}
            </div>
          </div>
          {verdict && (
            <span className={`pcf-verdict pcf-verdict-${verdict.tone}`}>{verdict.label}</span>
          )}
        </div>
      );
    }

    if (field.type === 'tier') {
      const opts = field.options || [];
      const selected = opts.find((o) => o.value === value) || null;
      return (
        <div className="pcf-tier">
          <div className="pcf-tier-track">
            {opts.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`pcf-tier-opt ${value === opt.value ? `pcf-tier-on pcf-tier-${opt.tone || 'mid'}` : ''}`}
                onClick={() => setField(field.id, opt.value)}
              >
                <span className="pcf-tier-num">{opt.value}</span>
                <span className="pcf-tier-label">{opt.label}</span>
              </button>
            ))}
          </div>
          <p className={`pcf-tier-desc ${selected ? `pcf-tier-desc-${selected.tone || 'mid'}` : ''}`}>
            {selected ? selected.desc : 'Stufe wählen für die Beschreibung.'}
          </p>
        </div>
      );
    }

    if (field.type === 'checklist') {
      const opts = field.options || [];
      const selected = Array.isArray(value) ? value : [];
      const score = checklistScore(field, value);
      const pct = score.maxPoints ? Math.round((score.points / score.maxPoints) * 100) : 0;
      const toggle = (optValue) => {
        const next = selected.includes(optValue)
          ? selected.filter((v) => v !== optValue)
          : [...selected, optValue];
        setField(field.id, next);
      };
      const richItems = !!field.descAlways;
      return (
        <div className={`pcf-checklist ${richItems ? 'pcf-checklist-rich' : ''}`}>
          <div className="pcf-checklist-items">
            {opts.map((opt) => {
              const on = selected.includes(opt.value);
              const showDesc = (on || richItems) && opt.desc;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`pcf-check ${on ? 'pcf-check-on' : ''} ${richItems ? 'pcf-check-rich' : ''}`}
                  onClick={() => toggle(opt.value)}
                >
                  <span className="pcf-check-box">{on ? '\u2713' : ''}</span>
                  <div className="pcf-check-content">
                    <span className="pcf-check-label">{opt.label}</span>
                    {showDesc && <p className="pcf-check-desc">{opt.desc}</p>}
                  </div>
                  {opt.points ? <span className="pcf-check-points">+{opt.points}</span> : null}
                </button>
              );
            })}
          </div>
          {score.maxPoints > 0 && (
            <div className="pcf-score">
              <div className="pcf-score-bar">
                <div className="pcf-score-fill" style={{ width: `${pct}%` }} />
              </div>
              <span className="pcf-score-value">{score.points} / {score.maxPoints} Punkte</span>
            </div>
          )}
        </div>
      );
    }

    if (field.type === 'protocol') {
      const current = value && typeof value === 'object' ? value : {};
      const mode = current.mode === 'text' ? 'text' : 'file';
      const setMode = (nextMode) => {
        if (nextMode === mode) return;
        setProtocolError('');
        if (nextMode === 'text') setField(field.id, { mode: 'text', text: current.text || '' });
        else setField(field.id, { mode: 'file', file: current.file || null });
      };
      const uploadFile = async (file) => {
        if (!file || protocolBusy) return;
        setProtocolBusy(true);
        setProtocolError('');
        try {
          const token = await resolveIdToken();
          const dataBase64 = await fileToBase64(file);
          const resp = await fetch(UPLOAD_FN_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({
              slug,
              linkKey,
              password: activePassword || '',
              fileName: file.name,
              contentType: file.type,
              dataBase64,
            }),
          });
          const data = await resp.json().catch(() => ({}));
          if (resp.status === 200 && data.ok && data.attachment) {
            setField(field.id, { mode: 'file', file: data.attachment });
          } else {
            setProtocolError(data.error || 'Upload fehlgeschlagen.');
          }
        } catch (e) {
          setProtocolError('Netzwerkfehler beim Upload.');
        } finally {
          setProtocolBusy(false);
        }
      };
      return (
        <div className="pcf-protocol">
          <div className="pcf-seg pcf-protocol-modes">
            <button
              type="button"
              className={`pcf-seg-opt ${mode === 'file' ? 'pcf-seg-on pcf-seg-good' : ''}`}
              onClick={() => setMode('file')}
            >
              Datei hochladen
            </button>
            <button
              type="button"
              className={`pcf-seg-opt ${mode === 'text' ? 'pcf-seg-on pcf-seg-good' : ''}`}
              onClick={() => setMode('text')}
            >
              Als Text einfügen
            </button>
          </div>
          {mode === 'file' ? (
            <div className="pcf-protocol-file">
              {current.file && current.file.url ? (
                <div className="pcf-protocol-attached">
                  <span className="pcf-protocol-name">{current.file.name}</span>
                  <button
                    type="button"
                    className="pcf-protocol-remove"
                    onClick={() => setField(field.id, { mode: 'file', file: null })}
                  >
                    Entfernen
                  </button>
                </div>
              ) : (
                <label className="pcf-protocol-drop">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.md,.html,.htm,.rtf,image/*"
                    hidden
                    disabled={protocolBusy}
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      if (f) uploadFile(f);
                      e.target.value = '';
                    }}
                  />
                  <span>{protocolBusy ? 'Wird hochgeladen…' : (field.fileHint || 'Transkript-Datei wählen (max. 5 MB)')}</span>
                </label>
              )}
            </div>
          ) : (
            <textarea
              className="pcf-textarea"
              rows={6}
              value={current.text || ''}
              placeholder={field.textPlaceholder || 'KI-Transkript hier einfügen …'}
              onChange={(e) => setField(field.id, { mode: 'text', text: e.target.value })}
            />
          )}
          {protocolError && <p className="pcf-protocol-error">{protocolError}</p>}
        </div>
      );
    }

    if (field.type === 'emails') {
      const max = field.max || 3;
      const texts = Array.isArray(value) ? value : [];
      const updateText = (idx, patch) => {
        const next = texts.map((t, i) => (i === idx ? { ...t, ...patch } : t));
        setField(field.id, next);
      };
      const addText = () => {
        if (texts.length >= max) return;
        setField(field.id, [...texts, { id: makeTextId(), subject: '', body: '' }]);
      };
      const removeText = (idx) => {
        setField(field.id, texts.filter((_, i) => i !== idx));
      };
      return (
        <div className="pcf-emails">
          {texts.map((t, idx) => (
            <div key={t.id || idx} className="pcf-email">
              <div className="pcf-email-head">
                <span className="pcf-email-no">Text {idx + 1}</span>
                <button type="button" className="pcf-email-remove" onClick={() => removeText(idx)} aria-label="Entfernen">×</button>
              </div>
              <input
                className="pcf-input pcf-email-subject"
                type="text"
                value={t.subject || ''}
                placeholder={field.subjectPlaceholder || 'Betreff'}
                onChange={(e) => updateText(idx, { subject: e.target.value })}
              />
              <textarea
                className="pcf-textarea pcf-email-body"
                rows={5}
                value={t.body || ''}
                placeholder={field.bodyPlaceholder || 'E-Mail-Text…'}
                onChange={(e) => updateText(idx, { body: e.target.value })}
              />
            </div>
          ))}
          {texts.length < max && (
            <button type="button" className="pcf-email-add" onClick={addText}>
              + Text hinzufügen ({texts.length}/{max})
            </button>
          )}
        </div>
      );
    }

    if (field.type === 'segment') {
      return (
        <div className="pcf-seg">
          {(field.options || []).map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`pcf-seg-opt ${value === opt.value ? `pcf-seg-on pcf-seg-${opt.tone || 'mid'}` : ''}`}
              onClick={() => setField(field.id, opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );
    }

    if (field.type === 'toggle') {
      // Default (noch nicht gesetzt) = an, damit das Audit standardmaessig sichtbar ist.
      const on = value !== 'no';
      return (
        <button
          type="button"
          className={`pcf-toggle ${on ? 'pcf-toggle-on' : ''}`}
          role="switch"
          aria-checked={on}
          onClick={() => setField(field.id, on ? 'no' : 'yes')}
        >
          <span className="pcf-toggle-track"><span className="pcf-toggle-knob" /></span>
          <span className="pcf-toggle-text">{on ? (field.onLabel || 'Ja') : (field.offLabel || 'Nein')}</span>
        </button>
      );
    }

    if (field.type === 'text') {
      return (
        <input
          className="pcf-input"
          type="text"
          value={value || ''}
          placeholder={field.placeholder || ''}
          onChange={(e) => setField(field.id, e.target.value)}
        />
      );
    }

    // note / textarea (default)
    return (
      <textarea
        className="pcf-textarea"
        rows={3}
        value={value || ''}
        placeholder={field.placeholder || ''}
        onChange={(e) => setField(field.id, e.target.value)}
      />
    );
  };

  // ── Guards ────────────────────────────────────────────────────
  if (!link || !form || !link.active) {
    return (
      <div className="pcf-wrap">
        <div className="pcf-state">
          <h1 className="pcf-state-title">Protokoll nicht verfügbar</h1>
          <p className="pcf-state-text">Dieser Link existiert nicht oder ist noch nicht aktiv.</p>
        </div>
      </div>
    );
  }

  if (phase === 'loading') {
    return (
      <div className="pcf-wrap">
        <div className="pcf-state">
          <div className="pcf-spinner" />
          <p className="pcf-state-text">Protokoll wird geladen…</p>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="pcf-wrap">
        <div className="pcf-state">
          <h1 className="pcf-state-title">Etwas ist schiefgelaufen</h1>
          <p className="pcf-state-text">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (phase === 'gate') {
    return (
      <div className="pcf-gate-screen">
        {/* Sanfte Hintergrund-Orbs */}
        <div className="pcf-gate-orbs" aria-hidden="true">
          <span className="pcf-gate-orb pcf-gate-orb-1" />
          <span className="pcf-gate-orb pcf-gate-orb-2" />
          <span className="pcf-gate-orb pcf-gate-orb-3" />
        </div>
        <div className="pcf-gate-cipher" aria-hidden="true" />

        <form className="pcf-gate-card" onSubmit={submitGate}>
          <div className="pcf-gate-vault" aria-hidden="true">
            <span className="pcf-gate-ring pcf-gate-ring-1" />
            <span className="pcf-gate-ring pcf-gate-ring-2" />
            <div className="pcf-gate-lock">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="11" width="16" height="9" rx="2" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" />
              </svg>
            </div>
          </div>

          <span className="pcf-gate-eyebrow">Protocool · Detailing-Call</span>
          <h1 className="pcf-gate-heading">
            <ScrambleText text="Geschütztes Protokoll" />
          </h1>
          <p className="pcf-gate-text">Bitte gib das Passwort ein, das du erhalten hast.</p>

          <div className={`pcf-gate-field ${gateError ? 'pcf-gate-field-error' : ''}`}>
            <span className="pcf-gate-field-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="11" width="16" height="9" rx="2" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" />
              </svg>
            </span>
            <input
              className="pcf-gate-field-input"
              type="text"
              autoComplete="off"
              autoFocus
              placeholder="XXXX-XXXX-XXXX-XXXX"
              value={passwordInput}
              onChange={(e) => { setPasswordInput(e.target.value); if (gateError) setGateError(''); }}
            />
          </div>
          {gateError && <span className="pcf-gate-field-err">{gateError}</span>}

          <button type="submit" className="pcf-gate-cta">
            <span className="pcf-gate-cta-label">Entsperren</span>
          </button>

          <span className="pcf-gate-foot">Vertraulich · nur für das SalesGod-Team</span>
        </form>
      </div>
    );
  }

  // phase === 'form'
  return (
    <div className="pcf-wrap">
      <div className="pcf-sheet">
        <header className="pcf-header">
          <h1 className="pcf-title">{form.title}</h1>
          <p className="pcf-company">{companyName}</p>
          {form.intro && <p className="pcf-intro">{form.intro}</p>}
          <div className="pcf-progress">
            <div className="pcf-progress-bar">
              <div className="pcf-progress-fill" style={{ width: `${completion.pct}%` }} />
            </div>
            <span className="pcf-progress-label">{completion.filled}/{completion.total} beantwortet</span>
          </div>
        </header>

        <div className="pcf-field pcf-field-name">
          <label className="pcf-label">Dein Name</label>
          <input
            className="pcf-input"
            type="text"
            value={filledBy}
            placeholder="Wer füllt aus?"
            onChange={(e) => { setFilledBy(e.target.value); setSavedTick(false); }}
          />
        </div>

        {form.sections.map((section) => (
          <section
            key={section.id}
            className="pcf-section"
            style={section.accent ? { '--pcf-stage': section.accent } : undefined}
          >
            <div className="pcf-section-head">
              {section.stage && <span className="pcf-section-stage">{section.stage}</span>}
              <h2 className="pcf-section-title">{section.title}</h2>
              {section.help && <p className="pcf-section-help">{section.help}</p>}
            </div>
            {(section.fields || [])
              .filter((field) => isFieldVisible(field, responses))
              .map((field) => (
                <div key={field.id} className="pcf-field">
                  <label className="pcf-label">
                    {field.label}
                    {field.required && <span className="pcf-required">*</span>}
                  </label>
                  {field.help && <p className="pcf-help">{field.help}</p>}
                  {renderField(field)}
                </div>
              ))}
          </section>
        ))}

        <footer className="pcf-footer">
          {submittedAt && missingFields.length > 0 ? (
            <div className="pcf-saved-warn">
              <span className="pcf-saved-warn-ic" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                </svg>
              </span>
              <div className="pcf-saved-warn-text">
                <span className="pcf-saved-warn-title">
                  Gespeichert &#10003; &mdash; aber {missingFields.length}{' '}
                  {missingFields.length === 1 ? 'Feld ist' : 'Felder sind'} noch leer
                </span>
                <span className="pcf-saved-warn-sub">
                  {missingRequired.length > 0
                    ? `Davon ${missingRequired.length} ${missingRequired.length === 1 ? 'Pflichtfeld' : 'Pflichtfelder'}. Du kannst trotzdem alles jederzeit nachtragen.`
                    : 'Kein Stress &ndash; du kannst sie jederzeit nachtragen.'}
                </span>
                <ul className="pcf-saved-warn-list">
                  {missingFields.slice(0, 6).map((f) => (
                    <li key={f.id} className={f.required ? 'pcf-saved-warn-req' : ''}>
                      {f.label}
                      {f.required && <span className="pcf-saved-warn-badge">Pflicht</span>}
                    </li>
                  ))}
                  {missingFields.length > 6 && (
                    <li className="pcf-saved-warn-more">+ {missingFields.length - 6} weitere</li>
                  )}
                </ul>
              </div>
            </div>
          ) : submittedAt ? (
            <div className="pcf-done">
              <span className="pcf-done-check" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </span>
              <div className="pcf-done-text">
                <span className="pcf-done-title">Du hast es erledigt! 🎉</span>
                <span className="pcf-done-sub">Alles sicher gespeichert &mdash; du kannst trotzdem jederzeit weiter editieren.</span>
              </div>
            </div>
          ) : (
            <div className="pcf-footer-meta">Noch nichts gespeichert.</div>
          )}
          <button
            type="button"
            className={`pcf-btn pcf-btn-primary ${savedTick ? 'pcf-btn-saved' : ''}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Speichern\u2026' : savedTick ? 'Gespeichert \u2713' : 'Speichern'}
          </button>
        </footer>
        {errorMsg && phase === 'form' && <div className="pcf-inline-error">{errorMsg}</div>}
      </div>
    </div>
  );
};

export default ProtocoolForm;
